import { Box, Text, Flex, VStack, Textarea, Switch, Button, Field } from '@chakra-ui/react';
import { size, trim } from 'lodash/fp';
import { useAppStore } from '@halo/chat-sdk-react';
import { toaster } from '@halo/ui-chat-react';
import info from 'virtual:build-info';
import { exportReport } from '@/scripts/debug/report';
import { useActionsStore } from '@/store/actions';
import { generateZip } from './fileHelper.ts';
import Select from './Select.tsx';
import Imgs from './Imgs.tsx';

function ReportForm() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState<string>();
  const [desc, setDesc] = useState<string>();
  const [imgs, setImgs] = useState<string[]>([]);
  const [logDevice, setLogDevice] = useState<boolean>(true);
  const client = useAppStore((state) => state.client);
  const onLogReportOpenChange = useActionsStore((set) => set.onLogReportOpenChange);
  const descValid = useMemo(() => trim(desc ?? '') && size(desc ?? '') > 19, [desc]);
  const disabled = useMemo(() => !type || !descValid, [descValid, type]);

  const [descFieldChanged, setDescFiledChanged] = useState(false);

  const onSub = async () => {
    let params: Record<any, any> = {
      client_platform: 'Web',
      app_version: info.version.replace(/-\d+/, ''),
      problem_type: Number(type),
      problem_description: trim(desc ?? ''),
      image_urls: imgs,
    };

    setLoading(true);
    try {
      if (logDevice) {
        let files: File[] = [],
          dbFile;
        try {
          files = await exportReport();
        } catch (_) {
          //
        }
        try {
          dbFile = await client?.Api.common.getDeviceLog();
        } catch (_) {
          //
        }
        if (size(files) || dbFile) {
          const zipFile = await generateZip(files, dbFile);
          const log_file_url = await client?.Api.common.uploadLog(zipFile);
          params = { ...params, log_file_url };
        }
      }
      await client?.Api.common.logReport({ ...params });
      toaster.success({ title: t('subSuc') });
      onLogReportOpenChange(false);
    } catch (_e) {
      toaster.error({ title: t('subFail') });
    } finally {
      setLoading(false);
    }
  };

  return (
    <VStack gap={2} w="full" fontSize={'xs'}>
      <Box w="full">
        <Flex h={'36px'} gap={1} alignItems={'center'}>
          <Text color="red.100">* </Text>
          <Text fontWeight={500} color="grey.95">
            {t('type')}
          </Text>
        </Flex>
        <Select
          items={[
            { label: t('logReportType.msg'), value: '1' },
            { label: t('logReportType.call'), value: '2' },
            { label: t('logReportType.media'), value: '3' },
            { label: t('logReportType.wallet'), value: '4' },
            { label: t('logReportType.crash'), value: '5' },
            { label: t('logReportType.others'), value: '6' },
          ]}
          value={type ? [type] : undefined}
          onSelect={(detail) => setType(detail.value)}
          placeholder={t('reportTypeSelectPlaceholder')}
        />
      </Box>
      <Box w="full">
        <Field.Root invalid={descFieldChanged && !descValid}>
          <Flex h={'36px'} gap={1} alignItems={'center'}>
            <Text color="#FF3B3B">*</Text>
            <Text fontWeight={500} color="grey.95">
              {t('chatSettings.description')}
            </Text>
          </Flex>
          <Textarea
            placeholder={t('chatSettings.descriptionPlaceholder')}
            variant="subtle"
            maxLength={1000}
            background="bg"
            rows={6}
            bg="white"
            size="xl"
            fontSize={'sm'}
            value={desc}
            css={{
              '--radii-l2': '6px',
              padding: '12px',
            }}
            onChange={({ target: { value } }) => {
              if (!descFieldChanged) {
                setDescFiledChanged(true);
              }
              setDesc(value);
            }}
          />
          <Flex justify="space-between" w="full" align="flex-start">
            <Field.ErrorText flex="1">
              {!trim(desc || '')
                ? t('chatSettings.descriptionRequired')
                : t('chatSettings.descriptionMinLength')}
            </Field.ErrorText>
            <Field.HelperText as="div" ms="auto" flexShrink={0}>
              {size(desc)}/1000
            </Field.HelperText>
          </Flex>
        </Field.Root>
      </Box>

      <Box w="full">
        <Imgs images={imgs} setImages={setImgs} />
      </Box>

      <Box mt={'60px'} w="full">
        <Box
          height={'54px'}
          display={'flex'}
          alignItems={'center'}
          justifyContent={'space-between'}
          px={'20px'}
          w="100%"
          background={'white'}
          border="1px solid"
          borderColor={'grey.50'}
          borderRadius={'8px'}
        >
          <Text color={'grey.97'} fontWeight={500} fontSize={'md'}>
            {t('logDevice')}
          </Text>
          <Switch.Root
            checked={logDevice}
            onCheckedChange={(e) => {
              setLogDevice(e.checked);
            }}
          >
            <Switch.HiddenInput />
            <Switch.Control />
          </Switch.Root>
        </Box>

        <Box py={'10px'}>
          <Text fontSize={'xs'} fontWeight={500} color={'grey.92'}>
            {t('logDeviceDesc')}
          </Text>
        </Box>
      </Box>

      <Button
        disabled={disabled}
        borderRadius={'10px'}
        px={'50px'}
        loading={loading}
        onClick={onSub}
      >
        {t('buttons.confirm')}
      </Button>
    </VStack>
  );
}

export default ReportForm;
